import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import axios from "axios";
import API_ENDPOINTS from "config/api";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

export default function ReportesContables() {
  const [tipoReporte, setTipoReporte] = useState("auxiliar");
  const [fechaInicio, setFechaInicio] = useState("2024-11-01");
  const [fechaFin, setFechaFin] = useState("2024-11-30");
  const [loading, setLoading] = useState(false);
  const [datosReporte, setDatosReporte] = useState({ rows: [], totales: { debe: 0, haber: 0 } });

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_ENDPOINTS.CONTABILIDAD.REPORTES, {
        params: {
          tipo: tipoReporte,
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
        },
      });
      setDatosReporte(response.data);
    } catch (error) {
      console.error("Error al cargar reporte contable:", error);
      // Fallback a datos de ejemplo en caso de error
      if (tipoReporte === "auxiliar") {
        setDatosReporte({
          rows: [
            {
              cuenta: "1101 - Caja",
              saldoInicial: 50000,
              debe: 25000,
              haber: 8000,
              saldoFinal: 67000,
            },
            {
              cuenta: "1102 - Bancos",
              saldoInicial: 450000,
              debe: 80000,
              haber: 95000,
              saldoFinal: 435000,
            },
          ],
          totales: { debe: 105000, haber: 103000 },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [tipoReporte]);

  const reportesDisponibles = [
    { value: "auxiliar", label: "Balanza de Comprobación", icon: "balance" },
    { value: "diario", label: "Libro Diario", icon: "menu_book" },
    { value: "antiguedad", label: "Antigüedad de Saldos", icon: "access_time" },
    { value: "comparativo", label: "Análisis Comparativo", icon: "compare_arrows" },
    { value: "ratios", label: "Ratios Financieros", icon: "trending_up" },
  ];

  const datosBalanza = [
    {
      cuenta: "1101 - Caja",
      saldoInicial: 50000,
      debe: 25000,
      haber: 8000,
      saldoFinal: 67000,
    },
    {
      cuenta: "1102 - Bancos",
      saldoInicial: 450000,
      debe: 80000,
      haber: 95000,
      saldoFinal: 435000,
    },
    {
      cuenta: "1103 - Cuentas por Cobrar",
      saldoInicial: 85000,
      debe: 45000,
      haber: 35000,
      saldoFinal: 95000,
    },
    {
      cuenta: "4100 - Ingresos",
      saldoInicial: 0,
      debe: 0,
      haber: 320000,
      saldoFinal: 320000,
    },
    {
      cuenta: "5101 - Sueldos",
      saldoInicial: 0,
      debe: 75000,
      haber: 0,
      saldoFinal: 75000,
    },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  const columns = [
    { Header: "cuenta", accessor: "cuenta", width: "30%", align: "left" },
    { Header: "saldo inicial", accessor: "saldoInicial", align: "right" },
    { Header: "debe", accessor: "debe", align: "right" },
    { Header: "haber", accessor: "haber", align: "right" },
    { Header: "saldo final", accessor: "saldoFinal", align: "right" },
  ];

  const dataRows = datosReporte.rows || [];
  const rows = dataRows.map((item) => ({
    cuenta: (
      <MDTypography variant="caption" fontWeight="medium">
        {item.cuenta}
      </MDTypography>
    ),
    saldoInicial: (
      <MDTypography variant="caption" fontWeight="medium">
        {formatCurrency(item.saldoInicial)}
      </MDTypography>
    ),
    debe: (
      <MDTypography variant="caption" fontWeight="medium" color="success">
        {formatCurrency(item.debe)}
      </MDTypography>
    ),
    haber: (
      <MDTypography variant="caption" fontWeight="medium" color="error">
        {formatCurrency(item.haber)}
      </MDTypography>
    ),
    saldoFinal: (
      <MDTypography variant="caption" fontWeight="bold">
        {formatCurrency(item.saldoFinal)}
      </MDTypography>
    ),
  }));

  const totalDebe = datosReporte.totales?.debe || 0;
  const totalHaber = datosReporte.totales?.haber || 0;

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDTypography variant="h6" fontWeight="medium">
                  Reportes Contables
                </MDTypography>
                <MDBox display="flex" gap={2}>
                  <MDButton variant="gradient" color="success" startIcon={<Icon>download</Icon>}>
                    Exportar Excel
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="error"
                    startIcon={<Icon>picture_as_pdf</Icon>}
                  >
                    Exportar PDF
                  </MDButton>
                </MDBox>
              </MDBox>

              {/* Selector de reportes */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Tipo de Reporte"
                    value={tipoReporte}
                    onChange={(e) => setTipoReporte(e.target.value)}
                  >
                    {reportesDisponibles.map((reporte) => (
                      <MenuItem key={reporte.value} value={reporte.value}>
                        <Icon sx={{ mr: 1 }}>{reporte.icon}</Icon>
                        {reporte.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fecha Inicio"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fecha Fin"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    fullWidth
                    sx={{ height: "100%" }}
                    onClick={fetchReportData}
                    disabled={loading}
                  >
                    <Icon>{loading ? "autorenew" : "search"}</Icon>&nbsp;
                    {loading ? "Cargando..." : "Generar"}
                  </MDButton>
                </Grid>
              </Grid>

              {/* Tarjetas de reportes disponibles */}
              <Grid container spacing={2} mb={3}>
                {reportesDisponibles.map((reporte) => (
                  <Grid item xs={12} sm={6} md={4} lg={2.4} key={reporte.value}>
                    <Card
                      variant="outlined"
                      sx={{
                        cursor: "pointer",
                        transition: "all 0.3s",
                        bgcolor: tipoReporte === reporte.value ? "info.main" : "transparent",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: 3,
                        },
                      }}
                      onClick={() => setTipoReporte(reporte.value)}
                    >
                      <MDBox p={2} textAlign="center">
                        <Icon
                          fontSize="large"
                          sx={{
                            color: tipoReporte === reporte.value ? "white" : "info.main",
                          }}
                        >
                          {reporte.icon}
                        </Icon>
                        <MDTypography
                          variant="caption"
                          fontWeight="medium"
                          color={tipoReporte === reporte.value ? "white" : "text"}
                          display="block"
                          mt={1}
                        >
                          {reporte.label}
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Contenido del reporte (ejemplo: Balanza de Comprobación) */}
              {tipoReporte === "auxiliar" && (
                <>
                  <MDBox mb={2}>
                    <MDTypography variant="h6" fontWeight="medium">
                      Balanza de Comprobación
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      Periodo: {new Date(fechaInicio).toLocaleDateString("es-MX")} -{" "}
                      {new Date(fechaFin).toLocaleDateString("es-MX")}
                    </MDTypography>
                  </MDBox>

                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />

                  {/* Totales */}
                  <MDBox
                    display="flex"
                    justifyContent="flex-end"
                    mt={2}
                    p={2}
                    sx={{ bgcolor: "grey.100", borderRadius: 1 }}
                  >
                    <Grid container spacing={2} justifyContent="flex-end">
                      <Grid item>
                        <MDTypography variant="button" fontWeight="bold">
                          Total Debe:
                        </MDTypography>
                        <MDTypography variant="button" fontWeight="bold" color="success" ml={2}>
                          {formatCurrency(totalDebe)}
                        </MDTypography>
                      </Grid>
                      <Grid item>
                        <MDTypography variant="button" fontWeight="bold">
                          Total Haber:
                        </MDTypography>
                        <MDTypography variant="button" fontWeight="bold" color="error" ml={2}>
                          {formatCurrency(totalHaber)}
                        </MDTypography>
                      </Grid>
                      <Grid item>
                        <Chip
                          label={
                            totalDebe === totalHaber
                              ? "✓ Balanza Cuadrada"
                              : "⚠ Balanza Descuadrada"
                          }
                          color={totalDebe === totalHaber ? "success" : "error"}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>
                </>
              )}

              {/* Otros reportes */}
              {tipoReporte !== "auxiliar" && (
                <MDBox textAlign="center" py={8}>
                  <Icon fontSize="large" color="action">
                    {reportesDisponibles.find((r) => r.value === tipoReporte)?.icon}
                  </Icon>
                  <MDTypography variant="h5" color="text" mt={2}>
                    {reportesDisponibles.find((r) => r.value === tipoReporte)?.label}
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mt={1}>
                    Este reporte está en desarrollo
                  </MDTypography>
                  <MDButton variant="gradient" color="info" sx={{ mt: 3 }}>
                    Generar Reporte
                  </MDButton>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
